// ink-component
import React, { ReactNode } from 'react';
import HStack from '../../../HStack/HStack';
import Actions from '../LedgerWrapperActions/LedgerWrapperActions';
import Search, { SearchProps } from '../LedgerWrapperSearch/LedgerWrapperSearch';
import Filter, { FilterItems } from '../LedgerWrapperFilter/LedgerWrapperFilter';
import Button from '../../../Button/Button';
import Text from '../../../Typography/Text/Text';
import { UseSelectableActionsType } from '../../../NewTable/hooks/useSelectable';

export interface TopBarProps extends SearchProps {
  /** Buttons, dropdowns, or other actions-type components that render when no rows are selected. To pass more than one component, wrap them with a fragment as in `actions={<>...</>}` */
  actions?: ReactNode;
  /** Actions for when some rows on the ledger are checked. To pass more than one component, wrap them with a fragment as in `bulkActions={<>...</>}` */
  bulkActions?: ReactNode;
  /** The ledger's filter items. */
  filter?: FilterItems;
  /** Control whether or not there is a search bar  */
  hasSearch?: boolean;
  /** Object representing which rows have been selected. Comes from useLedger or useSelectable hook. */
  rows: Record<string, unknown>;
  /** Selectable items' actions. Comes from useLedger or useSelectable hook */
  selectableProps: UseSelectableActionsType;
}

const ClearSelection = ({ selectedRows, totalRows, onClick }) => (
  <div>
    <Text variant="body-3-feedback-neutral" trim>
      {selectedRows} of {totalRows} items selected
    </Text>
    <Button onClick={onClick} type="link">
      Clear selection
    </Button>
  </div>
);

/**
 * @purpose Ledger top bar, complete with conditional rendering logic
 * @categories table
 * @group ledger
 */
const TopBar = (props: TopBarProps) => {
  const { rows, filter, actions, hasSearch, bulkActions, selectableProps, ...otherProps } = props;

  const selectedRows = Object.entries(rows).filter(([key, val]) => val);
  const rowsAreSelected = Boolean(selectedRows.length);
  const data = Object.keys(rows);

  const unselectedState = Object.keys(rows).reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

  /* When rows are selected and bulk actions were defined, they're rendered */
  if (rowsAreSelected && bulkActions) {
    return (
      <Actions alignY="top">
        {bulkActions}
        <ClearSelection
          selectedRows={selectedRows.length}
          totalRows={data.length}
          onClick={() => selectableProps.setSelectedRows(unselectedState)}
        />
      </Actions>
    );
  }

  /* When no rows are selected, the default actions are rendered */
  return (
    <Actions align="distributed">
      <HStack spacing="medium">
        {hasSearch && <Search placeholder="Search" {...otherProps} />}
        {filter && filter.groups && filter.groups.length > 0 && <Filter items={filter} {...otherProps} />}
      </HStack>
      {actions && <HStack spacing="medium">{actions}</HStack>}
    </Actions>
  );
};

TopBar.defaultProps = {
  actions: null,
  bulkActions: null,
  filter: null,
  hasSearch: true,
};

TopBar.displayName = 'LedgerWrapper.TopBar';

export default TopBar;
